import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * The URL ↔ state engine of the generic list page (05 §1.2).
 *
 * Generalized from the shipped `Spells.js`, which is the only list page that
 * ever had a central `updateFilter`; the other nine grew a closure per control.
 * Everything it enforces is one of the nav contract's four points (CLAUDE.md,
 * 05 §4.3), and each is a rule the hand-written pages got right only sometimes:
 *
 *  1. **One write per user action.** `updateFilter` sets state, resets `page`,
 *     and calls `setSearchParams` ONCE. Sequential writes each rebuild
 *     `URLSearchParams` from the same render's `searchParams`, so only the last
 *     survives and the history fills with intermediates — the ~22-entry
 *     clear-all bug (`Spells.js:343-377`).
 *  2. **Nothing writes a default.** State initializes from
 *     `restored[key] || searchParams.get(url_key) || ''` and only user actions
 *     write, so the mount-time-`updateFilter` pitfall (CLAUDE.md, commit
 *     5ac213f) is structurally impossible rather than merely avoided.
 *  3. **Aliases are read, never written.** `?class=` and `?class_name=` both
 *     initialize the class filter; the URL is always rewritten with the
 *     `url_key`. That is what keeps links shared in the wild alive without
 *     doubling the contract (04 §3.3).
 *  4. **The URL → state resync skips the first render**, so browser
 *     back/forward updates the controls while a `useListNav` restore is not
 *     clobbered.
 *
 * CSV IS THE MULTI-SELECT WIRE FORMAT, in the URL and to the API. `?level=1,2`
 * is the shipped contract and the array identity is preserved when the decoded
 * value has not changed, so a resync cannot refire the fetch effect.
 */

const MULTI = 'multi-select';
const YES_NO = 'yes-no';
// `presence` (issue 77) shares the tri-state's URL value contract wholesale:
// it writes `yes`/`no` and reads the boolean spellings as aliases, so a link
// carrying `?spellcasting=true` resolves and rewrites to the one spelling.
const PRESENCE = 'presence';

/**
 * A tri-state's URL VALUE has two shipped spellings, and both must keep working.
 *
 * The six spell component tri-states have always written `?verbal=yes`, and
 * feats' has-prerequisites tri-state has always written
 * `?has_prerequisites=true` — one control style, two vocabularies, because the
 * two pages were written separately. The generic control writes `yes`/`no`, so
 * the boolean spellings are READ and never written, exactly as a `url_alias` is:
 * a link shared or bookmarked in the wild resolves, and the URL it rewrites to
 * says one thing. The API accepts both for the same reason.
 */
const YES_NO_ALIASES = { true: 'yes', 1: 'yes', false: 'no', 0: 'no' };

const decode = (filter, raw) => {
  if (filter.style === MULTI) {
    return String(raw || '').split(',').map((v) => v.trim()).filter(Boolean);
  }
  if ((filter.style === YES_NO || filter.style === PRESENCE) && raw) {
    return YES_NO_ALIASES[String(raw).toLowerCase()] || String(raw);
  }
  return raw || '';
};

const encode = (value) => (Array.isArray(value) ? value.join(',') : (value || ''));

const isActive = (value) => (Array.isArray(value) ? value.length > 0 : value !== '' && value != null);

/** The first URL spelling that carries a value: the `url_key`, then its aliases. */
function readFromParams(searchParams, filter) {
  for (const key of [filter.urlKey, ...(filter.urlAliases || [])]) {
    const raw = searchParams.get(key);
    if (raw) return raw;
  }
  return '';
}

/**
 * A filter's value when the URL says nothing — `''` for almost every control,
 * and the authored `filter_default` for the `select` style (05 §3).
 *
 * THE DEFAULT IS REPRESENTED BY ABSENCE, and that is the whole contract Items'
 * `?property_filter=` has always had (`Items.js:263-265`). A non-empty default
 * means the control is never "off", so writing it to the URL would make a
 * pristine link and a cleared link disagree about a filter neither user
 * touched. So `hide` initializes the control, is sent to the API, and is never
 * written to the URL — and `encode` below is where the last of those three
 * lives.
 */
function defaultOf(filter) {
  return filter.filterDefault !== undefined && filter.filterDefault !== null
    ? filter.filterDefault : '';
}

/**
 * @param {object[]} filters  the Family's panel filters, plus any platform
 *                            filter the page adds (`rulebook`)
 * @param {object}   restored the `useListNav` cache entry for this route
 */
export default function useListQueryState(filters, restored) {
  const [searchParams, setSearchParams] = useSearchParams();

  const [values, setValues] = useState(() => {
    const initial = {};
    for (const filter of filters) {
      const cached = restored.filters ? restored.filters[filter.urlKey] : undefined;
      const raw = readFromParams(searchParams, filter);
      initial[filter.urlKey] = cached !== undefined
        ? cached
        : (raw === '' ? defaultOf(filter) : decode(filter, raw));
    }
    return initial;
  });

  /**
   * Set one filter and mirror it, in a single `setSearchParams`.
   *
   * `page` is reset here rather than by the caller, because "changing a filter
   * returns you to page 1" is a contract of the list page and not of any
   * control — every shipped page that got it wrong got it wrong per control.
   */
  const updateFilter = useCallback((filter, value, extraParams = {}) => {
    setValues((prev) => ({ ...prev, [filter.urlKey]: value }));
    const next = new URLSearchParams(searchParams);
    const encoded = encode(value);
    // A non-empty default is absence in the URL, so choosing it explicitly and
    // never touching the control produce the same link.
    if (encoded === '' || encoded === defaultOf(filter)) next.delete(filter.urlKey);
    else next.set(filter.urlKey, encoded);
    // An alias is read, never written — leaving a stale `?class_name=` beside a
    // fresh `?class=` would make the URL say two things at once.
    for (const alias of filter.urlAliases || []) next.delete(alias);
    next.set('page', '1');
    Object.entries(extraParams).forEach(([key, v]) => {
      if (v === '' || v === null || v === undefined) next.delete(key);
      else next.set(key, String(v));
    });
    setSearchParams(next);
  }, [searchParams, setSearchParams]);

  /** Drop every filter's state without touching the URL (the caller clears it). */
  const resetValues = useCallback(() => {
    setValues(() => {
      const next = {};
      // Clearing returns each control to its DEFAULT, not to empty — which for
      // Items' property filter is `hide`, matching a pristine URL exactly.
      for (const filter of filters) {
        next[filter.urlKey] = Array.isArray(values[filter.urlKey]) ? [] : defaultOf(filter);
      }
      return next;
    });
  }, [filters, values]);

  // URL → state, skipping the first render (point 4 above).
  const syncedRef = useRef(searchParams.toString());
  useEffect(() => {
    if (syncedRef.current === searchParams.toString()) return;
    syncedRef.current = searchParams.toString();
    setValues((prev) => {
      const next = {};
      let changed = false;
      for (const filter of filters) {
        const raw = readFromParams(searchParams, filter);
        const decoded = raw === '' ? defaultOf(filter) : decode(filter, raw);
        const before = prev[filter.urlKey];
        // Preserve array identity when unchanged so the fetch effect, which
        // depends on these values, does not refire on an unrelated param.
        if (Array.isArray(decoded) && Array.isArray(before)
            && decoded.join(',') === before.join(',')) {
          next[filter.urlKey] = before;
        } else {
          next[filter.urlKey] = decoded;
          if (decoded !== before) changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [searchParams, filters]);

  /** The query parameters the API call carries, keyed by each filter's api_key. */
  const apiParams = useCallback((extra = {}) => {
    const params = {};
    for (const filter of filters) {
      const value = values[filter.urlKey];
      if (!isActive(value)) continue;
      // `filter_unfiltered_values` sit in state and in the URL and never reach
      // the API — `all` is the explicit "show everything" escape from a
      // non-empty default, so sending it would filter for exactly what it means
      // not to.
      if ((filter.unfilteredValues || []).includes(value)) continue;
      params[filter.apiKey] = encode(value);
    }
    // The `range` style's custom bounds are their own parameters, carried by
    // the page rather than by a filter's own value (see `FilterControl.Range`).
    for (const [key, value] of Object.entries(extra)) {
      if (value !== '' && value !== null && value !== undefined) params[key] = String(value);
    }
    return params;
  }, [filters, values]);

  return { values, setValues, updateFilter, resetValues, apiParams, isActive };
}
